import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AppProviders } from './contexts/AppProviders';
import './services/testAuth'; // 테스트용 임포트
import { AwsConnectionTest } from './components/AwsConnectionTest';
import {
  ProtectedRoute,
  OwnerRoute,
  AdminRoute,
  ManagerRoute,
} from './domains/auth/components';
import HomePage from './components/HomePage';
import GateConnectPage from './pages/GateConnectPage';
import AttendMainPage from './components/AttendMainPage';
import NewAttendFranchiseHomePage from './components/NewAttendFranchiseHomePage';
import Dashboard from './components/Dashboard';
import UserProfile from './components/UserProfile';
import { Payment, PaymentPageV2 } from './domains/payment/components';
import Layout from './components/Layout';
// 학부모 시스템
import ParentRoutes from './domains/parent/routes/ParentRoutes';
import HqRoutes from './domains/hq/routes/HqRoutes';
import {
  OwnerLayoutV2,
  OwnerDashboard,
  OwnerDashboardV3,
  UserManagement,
  AttendanceManagement,
  SeatManagement,
  LockerManagement,
  SeatConfiguration,
  PricingManagement,
  OperationalRules,
  MonthlyCalendarPage,
  PatrolQrManagement,
  NotificationManagement,
  NotificationHistoryPage,
  NotificationCenter,
  GroupSeatManagement,
  OwnerSettings,
  VipManagement,
  MemberManagement,
  GroupSpaceManagement,
  StatisticsPage,
  RevenueAnalyticsPage,
  IntegratedLayoutManagement,
  IntegrationSettings,
  UsageManagementPage,
  StaffManagement,
  GateManagement,
  EntranceExitLogs,
  ConsultationDashboardPage,
  ConsultationRecordsPage,
  StudyAnalyticsPage,
  GoalManagementPage,
  LearningManagementPage,
  ParentCommunicationPage
} from './domains/owner/components';
import SubjectManagementPage from './domains/owner/pages/SubjectManagementPage';
import ErrorReportsPage from './domains/owner/pages/ErrorReportsPage';
import OwnerNoticesPage from './domains/owner/pages/OwnerNoticesPage';
import SupportChatPage from './domains/owner/pages/SupportChatPage';
import PaymentManagementPage from './domains/owner/pages/PaymentManagementPage';
import PaymentGuidePage from './domains/owner/pages/PaymentGuidePage';
import CounselingInsightPage from './domains/owner/pages/CounselingInsightPage';
import AttendanceSchedulePage from './domains/owner/pages/AttendanceSchedulePage';
import ApplicationManagementPage from './domains/owner/pages/ApplicationManagementPage';
import EntranceAnomaliesPage from './domains/owner/pages/EntranceAnomaliesPage';
import BellPage from './pages/BellPage';
import AttendanceMonitoringPage from './domains/owner/pages/AttendanceMonitoringPage';
import PeriodAttendanceGridPage from './domains/owner/pages/PeriodAttendanceGridPage';
import PatrolNoteManagementPage from './domains/owner/pages/PatrolNoteManagementPage';
import StaffTaskSettingsPage from './domains/owner/pages/StaffTaskSettingsPage';
import StaffMemoPage from './domains/owner/pages/StaffMemoPage';
import StaffHubPage from './domains/owner/pages/StaffHubPage';
import PeriodTemplateSettingsPage from './domains/owner/pages/PeriodTemplateSettingsPage';
import SuspendedPage from './domains/owner/pages/SuspendedPage';
import ExceptionRequestsPage from './pages/owner/ExceptionRequestsPage';
import PatrolAttendance from './domains/owner/components/PatrolAttendance';
import StudyRankings from './components/StudyRankings';
import KioskDisplay from './components/kiosk/KioskDisplay';
import ContractSignPage from './pages/ContractSignPage';
import { OwnerLogin, LoginPage } from './domains/auth/components';
import { AdminLoginPage } from './domains/admin/components/auth';
import KakaoCallback from './domains/auth/components/KakaoCallback';
import OwnerSignupPage from './pages/auth/OwnerSignupPage';
import OwnerApprovalPage from './pages/admin/OwnerApprovalPage';
import StudentLayoutView from './components/student/StudentLayoutView';
import ContactPage from './components/ContactPage';
import StudyRoomHomePage from './components/StudyRoomHomePage';
// Legacy Admin Components - 개별 import (export 충돌 방지)
import LegacyContactManagement from './domains/admin/components/legacy/ContactManagement';
import FacilityManagement from './domains/admin/components/legacy/FacilityManagement';
import OwnerManagement from './domains/admin/components/legacy/OwnerManagement';
import AnalyticsPage from './domains/admin/components/legacy/AnalyticsPage';
import SystemManagementPage from './domains/admin/components/legacy/SystemManagementPage';
import NotificationCenterPage from './domains/admin/components/legacy/NotificationCenterPage';
import ServicePlanManagement from './domains/admin/components/legacy/ServicePlanManagement';
import ApiUsageMonitoring from './domains/admin/components/legacy/ApiUsageMonitoring';
import DatabaseManagement from './domains/admin/components/legacy/DatabaseManagement';
import LegacyAdminDashboard from './domains/admin/components/legacy/AdminDashboard';
import LegacyAdminLayout from './domains/admin/components/legacy/AdminLayout';
import { AdminRoutes } from './domains/admin/routes';
import AdminPaymentManagement from './pages/admin/AdminPaymentManagement';
// 삭제됨: 웹 태블릿 버전 (React Native 앱으로 대체)
// import TabletGateSetup from './components/tablet/TabletGateSetup';
// import TabletGateOperation from './components/tablet/TabletGateOperation';
import {
  PaymentSuccess,
  PaymentCancel,
  PaymentFail,
  TossPaymentSuccess,
  TossPaymentFail,
  SubscriptionPlans,
  SubscriptionCheckout,
  SubscriptionSuccess,
  SubscriptionManage,
  BillingKeyRegister,
  PaymentFailureRecovery
} from './domains/payment/components';
import { PenaltyManagement } from './domains/owner/components/penalty';
import TimeTrackingPage from './pages/TimeTrackingPage';
import SeatReservationPage from './pages/SeatReservationPage';
import Logout from './components/Logout';
import ServiceProposalPage from './components/ServiceProposalPage';
import StudentStatisticsPage from './domains/student/pages/StudentStatisticsPage';
// 학생 레이아웃 및 페이지
import StudentLayout from './domains/student/components/layout/StudentLayout';
import StudentDashboard from './domains/student/pages/StudentDashboard';
import StudentAttendancePage from './domains/student/pages/AttendancePage';
import StudentStudyTimePage from './domains/student/pages/StudyTimePage';
import StudentPenaltyPage from './domains/student/pages/PenaltyPage';
import StudentApplicationPage from './domains/student/pages/ApplicationPage';
import StudentSchedulePage from './domains/student/pages/SchedulePage';
import StudentPeriodSchedulePage from './domains/student/pages/PeriodSchedulePage';
import StudentSettingsPage from './domains/student/pages/SettingsPage';
import DisplayBoardPage from './pages/DisplayBoardPage';  // ⭐ 원별 TV 전광판 (공개)
import PatrolScanPage from './pages/PatrolScanPage';  // ⭐ 순찰 QR 찍기 (2026-09-23)
import StudentChatPage from './domains/student/pages/ChatPage';
// 통합 로그인 페이지
import UnifiedLoginPage from './domains/auth/pages/UnifiedLoginPage';
import LandingPage from './pages/LandingPage';
import SeatLayoutManagement from './pages/owner/SeatLayoutManagement';
import SeatAssignmentPage from './pages/owner/SeatAssignmentPage';
import SeatOperationPage from './pages/owner/SeatOperationPage';
import DemoAttendanceSchedulePage from './pages/DemoAttendanceSchedulePage';
import DemoAttendanceMonitoringPage from './pages/DemoAttendanceMonitoringPage';
import DemoMembersPage from './pages/DemoMembersPage';
import DemoEntranceExitPage from './pages/DemoEntranceExitPage';
import DemoConsultationPage from './pages/DemoConsultationPage';
import DemoIntegratedLayoutPage from './pages/DemoIntegratedLayoutPage';
import DemoGatesPage from './pages/DemoGatesPage';
import DemoBusinessPage from './pages/DemoBusinessPage';
import DemoConsultationDashboardPage from './pages/DemoConsultationDashboardPage';
import DemoGoalManagementPage from './pages/DemoGoalManagementPage';
import DemoStudyAnalyticsPage from './pages/DemoStudyAnalyticsPage';
import DemoSubjectManagementPage from './pages/DemoSubjectManagementPage';
import { AxiosErrorType } from './types/index';
import NewBuildWatcher from './components/NewBuildWatcher';
import { getAppMode, getDomainConfig, getCurrentDomain } from './config/domain';

// OAuth 콜백 리다이렉트 컴포넌트
function OAuthRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Supabase OAuth code 감지 (query parameter)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    // 현재 경로가 '/'이고 code 파라미터가 있으면 콜백으로 리다이렉트
    if (code && location.pathname === '/') {
      navigate(`/auth/kakao/callback${window.location.search}${window.location.hash}`, { replace: true });
      return;
    }

    // Supabase OAuth access_token 감지 (hash fragment) - 학부모 카카오 로그인
    const hash = window.location.hash;
    if (hash && hash.includes('access_token=') && location.pathname === '/') {
      navigate(`/parent/auth/kakao/callback${hash}`, { replace: true });
      return;
    }
  }, [navigate, location]);

  return null;
}

function App() {
  // React Router 디버깅 활성화
  if (process.env.NODE_ENV === 'development') {
    (window as any).__REACT_ROUTER_V6_DEV__ = true;
  }

  // 서브도메인 감지 - 정확한 조건으로 처리
  const hostname = window.location.hostname;
  const port = window.location.port;
  // 내부망에서는 경로로 구분
  const pathname = window.location.pathname;

  // 스테이징/내부망에서는 /studyroom 경로로 독서실 구분
  const isInternalNetwork = (
    hostname === '192.168.0.38' ||  // 스테이징: 192.168.0.38 (내부망)
    hostname === '180.70.238.90' ||  // 스테이징: 180.70.238.90 (외부망)
    hostname === 'staging.local' ||
    hostname.startsWith('192.168.') ||  // 모든 내부망 IP (로컬 윈도우 포함)
    (hostname === 'localhost' && port === '8080')  // 로컬 윈도우 localhost 접속
  );

  // 독서실 도메인 판단:
  // 1. studyroom 서브도메인인 경우
  // 2. 내부망에서 /studyroom 경로로 접근한 경우
  // 3. 스테이징 환경 IP 접속
  // 4. 로컬 개발 환경
  const isStudyRoomDomain = (
    hostname === 'studyroom.newattend.com' ||  // 운영: studyroom.newattend.com
    hostname === 'app.newattend.com' ||  // 운영: app.newattend.com (오너/학생/학부모 통합 앱)
    hostname === 'stg.newattend.com' ||  // STG: 스테이징 환경 (NTD 서버)
    hostname === 'studyroom.faceattend.store' ||  // 개발: studyroom.faceattend.store
    hostname.startsWith('studyroom.') ||  // studyroom.으로 시작하는 모든 서브도메인
    hostname === 'eruspace.newattend.com' ||  // 이루는 자리 독서실
    hostname === 'daegu-school.newattend.com' ||  // 대구고등학교 독서실
    hostname === 'studyroom.staging.local' ||  // 스테이징: studyroom.staging.local
    hostname === '192.168.0.38' ||  // 스테이징 내부 IP
    hostname === '180.70.238.90' ||  // 스테이징 외부 IP
    hostname === '43.201.86.180' ||  // 운영 EC2 직접 IP
    process.env.REACT_APP_ENVIRONMENT === 'local_windows' ||  // 로컬 윈도우: 독서실만
    process.env.REACT_APP_ENVIRONMENT === 'local_ubuntu' ||  // 로컬 우분투: 독서실만
    isInternalNetwork  // 내부망 접속 시 모두 독서실 도메인으로 처리
  );

  // 2026-05-06: 본부(HQ) 전용 서브도메인
  const isHqDomain = (hostname === 'hq.newattend.com');

  // 2026-05-06: NTD 회사 홈페이지 전용 서브도메인 (제품/회사 소개)
  const isNtdDomain = (hostname === 'ntd.newattend.com');

  // 2026-05-06: NewAttend 프랜차이즈 마케팅 홈페이지 (가맹모집/본부)
  // newattend.com 루트는 독서실과 별개 — 프랜차이즈 마케팅 페이지
  const isNewAttendMarketingDomain = (
    hostname === 'newattend.com' ||
    hostname === 'www.newattend.com'
  );

  // 디버깅용 콘솔 출력
  console.log('🔍 [Router Debug]', {
    hostname,
    isStudyRoomDomain,
    isHqDomain,
    pathname: window.location.pathname,
    href: window.location.href
  });

  // hq.newattend.com 은 HqRoutes 전용 트리로 완전 분리 (다른 라우트와 격리)
  if (isHqDomain) {
    return (
      <AppProviders>
        <Router>
          <NewBuildWatcher />
          <div className="App min-h-screen bg-gray-50">
            <Routes>
              <Route path="/*" element={<HqRoutes />} />
            </Routes>
          </div>
        </Router>
      </AppProviders>
    );
  }

  // ntd.newattend.com 은 NTD 회사 홈페이지 (제품/회사 소개) 전용
  if (isNtdDomain) {
    return (
      <AppProviders>
        <Router>
          <div className="App min-h-screen bg-gray-50">
            <Routes>
              <Route path="/*" element={<AttendMainPage />} />
            </Routes>
          </div>
        </Router>
      </AppProviders>
    );
  }

  // newattend.com / www.newattend.com 은 NewAttend 프랜차이즈 마케팅 홈페이지 전용
  if (isNewAttendMarketingDomain) {
    return (
      <AppProviders>
        <Router>
          <div className="App min-h-screen bg-gray-50">
            <Routes>
              <Route path="/*" element={<NewAttendFranchiseHomePage />} />
            </Routes>
          </div>
        </Router>
      </AppProviders>
    );
  }

  return (
    <AppProviders>
          <Router>
            <OAuthRedirectHandler />
            <NewBuildWatcher />
            <div className="App min-h-screen bg-gray-50">
            <Routes>
              {/* 레거시 /hq/* (메인 도메인 호환) */}
              <Route path="/hq/*" element={<HqRoutes />} />
              {/* 학부모 라우트 (공통) */}
              <Route path="/parent/*" element={<ParentRoutes />} />
              {/* 내부망용 /studyroom/parent 라우트 */}
              <Route path="/studyroom/parent/*" element={<ParentRoutes />} />
              {/* 복수형 경로도 지원 (리다이렉트) */}
              <Route path="/parents" element={<Navigate to="/parent/login" replace />} />
              <Route path="/parents/*" element={<Navigate to="/parent/login" replace />} />

              {/* 통합 로그인 페이지 */}
              <Route path="/login" element={<UnifiedLoginPage />} />
              <Route path="/studyroom/login" element={<UnifiedLoginPage />} />
              {/* ⭐ 2026-06-21 원별 TV 전광판 (공개, 토큰 기반) */}
              <Route path="/display/:token" element={<DisplayBoardPage />} />
              {/* ⭐ 2026-06-30 교시 종소리 (공개, 토큰 기반) */}
              <Route path="/bell/:token" element={<BellPage />} />
              {/* ⭐ 2026-09-23 순찰 QR 찍기 (공개 진입, 기록은 직원 로그인 필요) */}
              <Route path="/patrol-scan/:token" element={<PatrolScanPage />} />

              {/* 학생 라우트 */}
              <Route path="/student" element={<StudentLayout />}>
                <Route index element={<Navigate to="/student/dashboard" replace />} />
                <Route path="dashboard" element={<StudentDashboard />} />
                <Route path="attendance" element={<StudentAttendancePage />} />
                <Route path="study-time" element={<StudentStudyTimePage />} />
                <Route path="penalty" element={<StudentPenaltyPage />} />
                <Route path="applications" element={<StudentApplicationPage />} />
                <Route path="schedule" element={<StudentSchedulePage />} />
                <Route path="period-schedule" element={<StudentPeriodSchedulePage />} />
                <Route path="chat" element={<StudentChatPage />} />
                <Route path="settings" element={<StudentSettingsPage />} />
              </Route>
              {/* 내부망용 /studyroom/student 라우트 */}
              <Route path="/studyroom/student" element={<StudentLayout />}>
                <Route index element={<Navigate to="/studyroom/student/dashboard" replace />} />
                <Route path="dashboard" element={<StudentDashboard />} />
                <Route path="attendance" element={<StudentAttendancePage />} />
                <Route path="study-time" element={<StudentStudyTimePage />} />
                <Route path="penalty" element={<StudentPenaltyPage />} />
                <Route path="applications" element={<StudentApplicationPage />} />
                <Route path="schedule" element={<StudentSchedulePage />} />
                <Route path="period-schedule" element={<StudentPeriodSchedulePage />} />
                <Route path="chat" element={<StudentChatPage />} />
                <Route path="settings" element={<StudentSettingsPage />} />
              </Route>

              {/* 공통 라우트 (데모 페이지들) */}
              <Route path="/demo/attendance/schedule" element={<DemoAttendanceSchedulePage />} />
              <Route path="/demo/attendance/monitoring" element={<DemoAttendanceMonitoringPage />} />
              <Route path="/demo/members" element={<DemoMembersPage />} />
              <Route path="/demo/entrance-exit" element={<DemoEntranceExitPage />} />
              <Route path="/demo/consultation" element={<DemoConsultationPage />} />
              <Route path="/demo/integrated-layout" element={<DemoIntegratedLayoutPage />} />
              <Route path="/demo/gates" element={<DemoGatesPage />} />
              <Route path="/demo/business" element={<DemoBusinessPage />} />
              <Route path="/demo/consultation-dashboard" element={<DemoConsultationDashboardPage />} />
              <Route path="/demo/goal-management" element={<DemoGoalManagementPage />} />
              <Route path="/demo/study-analytics" element={<DemoStudyAnalyticsPage />} />
              <Route path="/demo/subject-management" element={<DemoSubjectManagementPage />} />

              {/* AWS 연결 테스트 페이지 */}
              <Route path="/aws-test" element={<AwsConnectionTest />} />

              {/* 게이트 연결 페이지 (공용) */}
              <Route path="/gate-connect" element={<GateConnectPage />} />

              {isStudyRoomDomain ? (
                // 독서실 서브도메인 라우트
                <React.Fragment key="studyroom-routes">
                  {/* 독서실 홈페이지 */}
                  <Route path="/" element={<StudyRoomHomePage />} />
                  <Route path="/studyroom" element={<StudyRoomHomePage />} />

                  {/* 독서실 전용 랜딩 페이지 */}
                  <Route path="/landing" element={<LandingPage />} />
                  
                  {/* 독서실 오너 로그인 */}
                  <Route path="/owner/login" element={<LoginPage />} />
                  <Route path="/studyroom/owner/login" element={<LoginPage />} />

                  {/* 오너 가입 신청 */}
                  <Route path="/owner/signup" element={<OwnerSignupPage />} />
                  <Route path="/studyroom/owner/signup" element={<OwnerSignupPage />} />

                  {/* 카카오 OAuth 콜백 */}
                  <Route path="/auth/kakao/callback" element={<KakaoCallback />} />
                  
                  {/* 학생 라우트 */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="seats" element={<StudentLayoutView />} />
                    <Route path="profile" element={<UserProfile />} />
                    <Route path="payment" element={<Payment />} />
                    <Route path="rankings" element={<StudyRankings />} />
                    <Route path="time-tracking" element={<TimeTrackingPage />} />
                    <Route path="seat-reservation" element={<SeatReservationPage />} />
                    <Route path="stats" element={<StudentStatisticsPage />} />
                  </Route>

                  {/* 직접 접근 라우트 */}
                  <Route
                    path="/seats"
                    element={
                      <ProtectedRoute>
                        <StudentLayoutView />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* 오너 라우트 */}
                  <Route
                    path="/owner/dashboard"
                    element={
                      <ManagerRoute>
                        <OwnerLayoutV2 />
                      </ManagerRoute>
                    }
                  >
                    <Route index element={<OwnerDashboardV3 />} />
                    <Route path="v2" element={<OwnerDashboard />} />
                    <Route path="seats" element={<SeatManagement />} />
                    <Route path="seat-config" element={<SeatConfiguration />} />
                    <Route path="groups" element={<GroupSeatManagement />} />
                    <Route path="pricing" element={<PricingManagement />} />
                    <Route path="rules" element={<OperationalRules />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="attendance" element={<AttendanceManagement />} />
                    <Route path="notifications" element={<NotificationHistoryPage />} />
                    <Route path="settings" element={<OwnerSettings />} />
                  </Route>

                  {/* 오너 직접 접근 라우트들 */}
                  <Route path="/owner/seats" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatManagement />} />
                  </Route>
                  {/* 2026-09-02 사물함 — 좌석과 별개 기능 */}
                  <Route path="/owner/lockers" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<LockerManagement />} />
                  </Route>
                  {/* DEPRECATED: seat-layout은 integrated-layout으로 통합됨 - 자동 리다이렉트 */}
                  <Route path="/owner/seat-layout" element={<Navigate to="/owner/integrated-layout" replace />} />
                  <Route path="/owner/seat-assignment" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatAssignmentPage />} />
                  </Route>
                  <Route path="/owner/seat-operation" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatOperationPage />} />
                  </Route>
                  {/* 월간 일정 (공휴일·수능·학사·매장 일정 달력) */}
                  <Route path="/owner/calendar" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<MonthlyCalendarPage />} />
                  </Route>
                  <Route path="/studyroom/owner/calendar" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<MonthlyCalendarPage />} />
                  </Route>
                  {/* 순찰 QR 관리 (2026-09-23) */}
                  <Route path="/owner/patrol-qr" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PatrolQrManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/patrol-qr" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PatrolQrManagement />} />
                  </Route>
                  <Route path="/owner/users" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<UserManagement />} />
                  </Route>
                  <Route path="/owner/attendance" element={<Navigate to="/owner/dashboard" replace />} />
                  <Route path="/owner/attendance/schedule" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<AttendanceSchedulePage />} />
                  </Route>
                  <Route path="/owner/period-settings" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PeriodTemplateSettingsPage />} />
                  </Route>
                  <Route path="/owner/attendance/monitoring" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<AttendanceMonitoringPage />} />
                  </Route>
                  <Route path="/owner/attendance/period-grid" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PeriodAttendanceGridPage />} />
                  </Route>
                  <Route path="/owner/attendance/patrol" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PatrolAttendance />} />
                  </Route>
                  <Route path="/owner/penalty" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PenaltyManagement />} />
                  </Route>
                  <Route path="/owner/exception-requests" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ExceptionRequestsPage />} />
                  </Route>
                  <Route path="/owner/patrol-notes" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PatrolNoteManagementPage />} />
                  </Route>
                  <Route path="/owner/staff-hub" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffHubPage />} />
                  </Route>
                  <Route path="/owner/staff-tasks" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffTaskSettingsPage />} />
                  </Route>
                  <Route path="/owner/staff-memos" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffMemoPage />} />
                  </Route>
                  <Route path="/owner/notifications" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<NotificationCenter />} />
                  </Route>
                  <Route path="/owner/members" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<MemberManagement />} />
                  </Route>
                  <Route path="/owner/applications" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ApplicationManagementPage />} />
                  </Route>
                  <Route path="/owner/entrance-exit" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<EntranceExitLogs />} />
                  </Route>
                  <Route path="/owner/entrance-anomalies" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<EntranceAnomaliesPage />} />
                  </Route>
                  <Route path="/owner/notification-history" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<NotificationHistoryPage />} />
                  </Route>
                  <Route path="/owner/error-reports" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ErrorReportsPage />} />
                  </Route>
                  <Route path="/owner/notices" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OwnerNoticesPage />} />
                  </Route>
                  <Route path="/owner/support" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SupportChatPage />} />
                  </Route>
                  <Route path="/owner/statistics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StatisticsPage />} />
                  </Route>
                  <Route path="/owner/analytics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<RevenueAnalyticsPage />} />
                  </Route>
                  
                  {/* 추가 오너 라우트들 */}
                  {(() => {
                    return null;
                  })()}
                  <Route path="/owner/integrated-layout" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<IntegratedLayoutManagement />} />
                  </Route>
                  <Route path="/owner/group-spaces" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GroupSpaceManagement />} />
                  </Route>
                  <Route path="/owner/gates" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GateManagement />} />
                  </Route>
                  <Route path="/owner/pricing" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PricingManagement />} />
                  </Route>
                  <Route path="/owner/vip" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<VipManagement />} />
                  </Route>
                  <Route path="/owner/usage" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<UsageManagementPage />} />
                  </Route>
                  <Route path="/owner/facility-info" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OwnerSettings />} />
                  </Route>
                  <Route path="/owner/rules" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OperationalRules />} />
                  </Route>
                  <Route path="/owner/staff" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffManagement />} />
                  </Route>
                  <Route path="/owner/integrations" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<IntegrationSettings />} />
                  </Route>

                  {/* 학습 관리 라우트 (통합 탭 인터페이스) */}
                  <Route path="/owner/learning-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<LearningManagementPage />} />
                  </Route>

                  {/* 결제 관리 라우트 (통합 탭 인터페이스) */}
                  <Route path="/owner/payment-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PaymentManagementPage />} />
                  </Route>

                  {/* 결제 안내 페이지 (플랜 신청 후 입금 안내) */}
                  <Route path="/owner/payment-guide" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PaymentGuidePage />} />
                  </Route>

                  {/* 플랜 선택 → 사용량 관리로 리다이렉트 */}
                  <Route path="/owner/plan-selection" element={<Navigate to="/owner/usage" replace />} />

                  {/* 클라우드 설정 → 사용량 관리로 리다이렉트 */}
                  <Route path="/owner/cloud-sync" element={<Navigate to="/owner/usage" replace />} />

                  {/* 기존 라우트 유지 (직접 URL 접근 허용) */}
                  <Route path="/owner/consultation-dashboard" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ConsultationDashboardPage />} />
                  </Route>
                  <Route path="/owner/consultation-records" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ConsultationRecordsPage />} />
                  </Route>
                  <Route path="/owner/study-analytics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StudyAnalyticsPage />} />
                  </Route>
                  <Route path="/owner/counseling-insights/:studentId" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<CounselingInsightPage />} />
                  </Route>
                  <Route path="/owner/goal-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GoalManagementPage />} />
                  </Route>
                  <Route path="/owner/subject-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SubjectManagementPage />} />
                  </Route>
                  <Route path="/owner/parent-communication" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ParentCommunicationPage />} />
                  </Route>

                  {/* 구독 정지/만료 안내 페이지 (레이아웃 없이 독립 페이지) */}
                  <Route path="/owner/suspended" element={<ManagerRoute><SuspendedPage /></ManagerRoute>} />

                  {/* 내부망용 /studyroom/owner 라우트들 */}
                  <Route path="/studyroom/owner/dashboard" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OwnerDashboardV3 />} />
                    <Route path="seats" element={<SeatManagement />} />
                    <Route path="seat-config" element={<SeatConfiguration />} />
                    <Route path="groups" element={<GroupSeatManagement />} />
                    <Route path="pricing" element={<PricingManagement />} />
                    <Route path="rules" element={<OperationalRules />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="attendance" element={<AttendanceManagement />} />
                    <Route path="notifications" element={<NotificationHistoryPage />} />
                    <Route path="settings" element={<OwnerSettings />} />
                  </Route>

                  {/* 내부망용 /studyroom/owner 직접 접근 라우트들 */}
                  <Route path="/studyroom/owner/seats" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatManagement />} />
                  </Route>
                  {/* DEPRECATED: seat-layout은 integrated-layout으로 통합됨 - 자동 리다이렉트 */}
                  <Route path="/studyroom/owner/seat-layout" element={<Navigate to="/studyroom/owner/integrated-layout" replace />} />
                  <Route path="/studyroom/owner/seat-assignment" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatAssignmentPage />} />
                  </Route>
                  <Route path="/studyroom/owner/seat-operation" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatOperationPage />} />
                  </Route>
                  <Route path="/studyroom/owner/users" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<UserManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/attendance" element={<Navigate to="/studyroom/owner/dashboard" replace />} />
                  <Route path="/studyroom/owner/attendance/schedule" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<AttendanceSchedulePage />} />
                  </Route>
                  <Route path="/studyroom/owner/period-settings" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PeriodTemplateSettingsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/attendance/monitoring" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<AttendanceMonitoringPage />} />
                  </Route>
                  <Route path="/studyroom/owner/attendance/period-grid" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PeriodAttendanceGridPage />} />
                  </Route>
                  <Route path="/studyroom/owner/attendance/patrol" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PatrolAttendance />} />
                  </Route>
                  <Route path="/studyroom/owner/penalty" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PenaltyManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/exception-requests" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ExceptionRequestsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/patrol-notes" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PatrolNoteManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/staff-hub" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffHubPage />} />
                  </Route>
                  <Route path="/studyroom/owner/staff-tasks" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffTaskSettingsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/staff-memos" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffMemoPage />} />
                  </Route>
                  <Route path="/studyroom/owner/notifications" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<NotificationCenter />} />
                  </Route>
                  <Route path="/studyroom/owner/members" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<MemberManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/entrance-exit" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<EntranceExitLogs />} />
                  </Route>
                  <Route path="/studyroom/owner/entrance-anomalies" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<EntranceAnomaliesPage />} />
                  </Route>
                  <Route path="/studyroom/owner/notification-history" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<NotificationHistoryPage />} />
                  </Route>
                  <Route path="/studyroom/owner/error-reports" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ErrorReportsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/notices" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OwnerNoticesPage />} />
                  </Route>
                  <Route path="/studyroom/owner/support" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SupportChatPage />} />
                  </Route>
                  <Route path="/studyroom/owner/statistics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StatisticsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/analytics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<RevenueAnalyticsPage />} />
                  </Route>
                  
                  {/* 내부망용 12개 오너 페이지 추가 라우트들 */}
                  <Route path="/studyroom/owner/integrated-layout" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<IntegratedLayoutManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/group-spaces" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GroupSpaceManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/gates" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GateManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/pricing" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PricingManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/vip" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<VipManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/usage" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<UsageManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/facility-info" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OwnerSettings />} />
                  </Route>
                  <Route path="/studyroom/owner/rules" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OperationalRules />} />
                  </Route>
                  <Route path="/studyroom/owner/staff" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/integrations" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<IntegrationSettings />} />
                  </Route>

                  {/* 내부망용 학습 관리 라우트 (통합 탭 인터페이스) */}
                  <Route path="/studyroom/owner/learning-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<LearningManagementPage />} />
                  </Route>

                  {/* 기존 라우트 유지 (직접 URL 접근 허용) */}
                  <Route path="/studyroom/owner/consultation-dashboard" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ConsultationDashboardPage />} />
                  </Route>
                  <Route path="/studyroom/owner/consultation-records" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ConsultationRecordsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/study-analytics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StudyAnalyticsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/goal-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GoalManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/subject-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SubjectManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/parent-communication" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ParentCommunicationPage />} />
                  </Route>
                  <Route path="/studyroom/owner/payment-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PaymentManagementPage />} />
                  </Route>

                  {/* 결제 안내 페이지 (플랜 신청 후 입금 안내) */}
                  <Route path="/studyroom/owner/payment-guide" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PaymentGuidePage />} />
                  </Route>

                  <Route path="/studyroom/owner/plan-selection" element={<Navigate to="/studyroom/owner/usage" replace />} />

                  {/* 클라우드 설정 페이지 */}
                  <Route path="/studyroom/owner/cloud-sync" element={<Navigate to="/studyroom/owner/usage" replace />} />

                  {/* 구독 정지/만료 안내 페이지 (레이아웃 없이 독립 페이지) */}
                  <Route path="/studyroom/owner/suspended" element={<ManagerRoute><SuspendedPage /></ManagerRoute>} />

                  {/* 공통 라우트 */}
                  <Route path="/logout" element={<Logout />} />
                  <Route path="/contact" element={<ContactPage />} />
                </React.Fragment>
              ) : (
                // 메인 도메인 라우트
                <React.Fragment key="main-routes">
                  {/* 어텐드 메인 홈페이지 (공개 접근) */}
                  <Route path="/" element={<AttendMainPage />} />

                  {/* 로컬/메인도메인에서 독서실 접근용 라우트 */}
                  <Route path="/studyroom" element={<StudyRoomHomePage />} />
                  <Route path="/studyroom/owner/login" element={<LoginPage />} />
                  <Route path="/studyroom/owner/signup" element={<OwnerSignupPage />} />

                  {/* /owner/* 경로를 /studyroom/owner/*로 리다이렉트 (localhost 호환성) */}
                  <Route path="/owner/login" element={<Navigate to="/studyroom/owner/login" replace />} />
                  <Route path="/owner/signup" element={<Navigate to="/studyroom/owner/signup" replace />} />
                  <Route path="/owner/dashboard/*" element={<Navigate to="/studyroom/owner/dashboard" replace />} />
                  <Route path="/owner/*" element={<Navigate to="/studyroom/owner/dashboard" replace />} />

                  {/* 로컬/메인도메인에서 오너 대시보드 라우트 */}
                  <Route path="/studyroom/owner/dashboard" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OwnerDashboardV3 />} />
                    <Route path="seats" element={<SeatManagement />} />
                    <Route path="seat-config" element={<SeatConfiguration />} />
                    <Route path="groups" element={<GroupSeatManagement />} />
                    <Route path="pricing" element={<PricingManagement />} />
                    <Route path="rules" element={<OperationalRules />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="attendance" element={<AttendanceManagement />} />
                    <Route path="notifications" element={<NotificationHistoryPage />} />
                    <Route path="settings" element={<OwnerSettings />} />
                  </Route>

                  {/* 로컬/메인도메인에서 오너 직접 접근 라우트들 */}
                  <Route path="/studyroom/owner/seats" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/seat-layout" element={<Navigate to="/studyroom/owner/integrated-layout" replace />} />
                  <Route path="/studyroom/owner/seat-assignment" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatAssignmentPage />} />
                  </Route>
                  <Route path="/studyroom/owner/seat-operation" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SeatOperationPage />} />
                  </Route>
                  <Route path="/studyroom/owner/users" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<UserManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/attendance" element={<Navigate to="/studyroom/owner/dashboard" replace />} />
                  <Route path="/studyroom/owner/attendance/schedule" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<AttendanceSchedulePage />} />
                  </Route>
                  <Route path="/studyroom/owner/period-settings" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PeriodTemplateSettingsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/attendance/monitoring" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<AttendanceMonitoringPage />} />
                  </Route>
                  <Route path="/studyroom/owner/attendance/period-grid" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PeriodAttendanceGridPage />} />
                  </Route>
                  <Route path="/studyroom/owner/attendance/patrol" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PatrolAttendance />} />
                  </Route>
                  <Route path="/studyroom/owner/penalty" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PenaltyManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/exception-requests" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ExceptionRequestsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/patrol-notes" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PatrolNoteManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/staff-hub" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffHubPage />} />
                  </Route>
                  <Route path="/studyroom/owner/staff-tasks" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffTaskSettingsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/staff-memos" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffMemoPage />} />
                  </Route>
                  <Route path="/studyroom/owner/notifications" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<NotificationCenter />} />
                  </Route>
                  <Route path="/studyroom/owner/members" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<MemberManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/entrance-exit" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<EntranceExitLogs />} />
                  </Route>
                  <Route path="/studyroom/owner/entrance-anomalies" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<EntranceAnomaliesPage />} />
                  </Route>
                  <Route path="/studyroom/owner/notification-history" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<NotificationHistoryPage />} />
                  </Route>
                  <Route path="/studyroom/owner/error-reports" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ErrorReportsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/notices" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OwnerNoticesPage />} />
                  </Route>
                  <Route path="/studyroom/owner/support" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SupportChatPage />} />
                  </Route>
                  <Route path="/studyroom/owner/statistics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StatisticsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/analytics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<RevenueAnalyticsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/integrated-layout" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<IntegratedLayoutManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/group-spaces" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GroupSpaceManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/gates" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GateManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/pricing" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PricingManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/vip" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<VipManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/usage" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<UsageManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/facility-info" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OwnerSettings />} />
                  </Route>
                  <Route path="/studyroom/owner/rules" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<OperationalRules />} />
                  </Route>
                  <Route path="/studyroom/owner/staff" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StaffManagement />} />
                  </Route>
                  <Route path="/studyroom/owner/integrations" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<IntegrationSettings />} />
                  </Route>
                  <Route path="/studyroom/owner/learning-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<LearningManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/consultation-dashboard" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ConsultationDashboardPage />} />
                  </Route>
                  <Route path="/studyroom/owner/consultation-records" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ConsultationRecordsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/study-analytics" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<StudyAnalyticsPage />} />
                  </Route>
                  <Route path="/studyroom/owner/goal-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<GoalManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/subject-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<SubjectManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/parent-communication" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<ParentCommunicationPage />} />
                  </Route>
                  <Route path="/studyroom/owner/payment-management" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PaymentManagementPage />} />
                  </Route>
                  <Route path="/studyroom/owner/payment-guide" element={<ManagerRoute><OwnerLayoutV2 /></ManagerRoute>}>
                    <Route index element={<PaymentGuidePage />} />
                  </Route>
                  <Route path="/studyroom/owner/plan-selection" element={<Navigate to="/studyroom/owner/usage" replace />} />
                  <Route path="/studyroom/owner/cloud-sync" element={<Navigate to="/studyroom/owner/usage" replace />} />
                  <Route path="/studyroom/owner/suspended" element={<ManagerRoute><SuspendedPage /></ManagerRoute>} />

                  {/* 랜딩 페이지 */}
                  <Route path="/landing" element={<LandingPage />} />

                  {/* 관리형 독서실 제안서 페이지 */}
                  <Route path="/managed-studyroom-proposal" element={<ServiceProposalPage />} />

                  {/* 어텐드 관리자 - 로그인 페이지 (인증 불필요) */}
                  <Route path="/admin/login" element={<AdminLoginPage />} />

                  {/* 어텐드 관리자 - 새로운 DDD 라우트 (인증 필요) */}
                  <Route
                    path="/admin/*"
                    element={
                      <AdminRoute>
                        <AdminRoutes />
                      </AdminRoute>
                    }
                  />

                  {/* 관리자 - 오너 가입 승인 페이지 */}
                  <Route
                    path="/admin/owner-approvals"
                    element={
                      <AdminRoute>
                        <OwnerApprovalPage />
                      </AdminRoute>
                    }
                  />

                  {/* 기존 레거시 관리자 라우트 (임시 유지) */}
                  <Route path="/admin/legacy/dashboard" element={<AdminRoute><LegacyAdminLayout /></AdminRoute>}>
                    <Route index element={<LegacyAdminDashboard />} />
                    <Route path="contacts" element={<LegacyContactManagement />} />
                    <Route path="owners" element={<OwnerManagement />} />
                    <Route path="facilities" element={<FacilityManagement />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="system" element={<SystemManagementPage />} />
                    <Route path="notifications" element={<NotificationCenterPage />} />
                    <Route path="service-plans" element={<ServicePlanManagement />} />
                    <Route path="api-monitoring" element={<ApiUsageMonitoring />} />
                    <Route path="payments" element={<AdminPaymentManagement />} />
                    <Route path="database" element={<DatabaseManagement />} />
                  </Route>
                  
                  {/* 공통 라우트 */}
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/logout" element={<Logout />} />
                </React.Fragment>
              )}
              
              {/* 삭제됨: 웹 태블릿 버전 - React Native 앱으로 대체 */}
              {/* <Route path="/tablet_gate_setup.html" element={<TabletGateSetup />} /> */}
              {/* <Route path="/tablet/gate-setup" element={<TabletGateSetup />} /> */}
              {/* <Route path="/tablet/gate-operation" element={<TabletGateOperation />} /> */}
              <Route path="/kiosk" element={<KioskDisplay />} />
              <Route path="/kiosk/display" element={<KioskDisplay />} />
              <Route path="/kiosk/fullscreen" element={<KioskDisplay />} />

              {/* 전자계약 서명 페이지 (공개 URL) */}
              <Route path="/contract/sign/:token" element={<ContractSignPage />} />

            </Routes>
            </div>
          </Router>
    </AppProviders>
  );
}

export default App;